/*
 * Snobum — Pin Products First
 * ---------------------------------------------------------------------------
 * ?first=<handle>[,<handle>,...] makes those products appear FIRST on a
 * collection page, in the order given, for ad landing links, without changing
 * the collection's saved order.
 *
 *   - Products already loaded on the page have their real cards moved up.
 *   - Products NOT on the page (deep in a big collection, past the 16-per-page
 *     limit) get a CLONE of a real card already on the page, repointed at them
 *     (image, title, price, link).
 *
 * Why clone instead of building HTML: the clone is a genuine card from your
 * CURRENT theme, so it automatically inherits any formatting you change later
 * (card style, image ratio, colours, fonts, spacing). Nothing to keep in sync.
 *
 *   One product:  /collections/ski?first=1349-usa-flag-snowboard
 *   Several:      /collections/ski?first=1349-cc1717,1287-cc1717,1301-cc1717
 *
 * Pins are capped at MAX_PINS = 16, one full collection page. At the cap the
 * whole first page is pinned cards and the collection proper starts on page 2,
 * so use the cap as a ceiling, not a target: pin what the ad actually showed.
 *
 * Scope:      Loaded only on collection pages (injected via the collection template).
 * Fail-safe:  If a handle is wrong, JS is off, a fetch fails, or there's no card
 *             to clone, the collection renders normally. It never hides products.
 *             A bad handle is skipped on its own; the other pins still land.
 *
 * Canonical copy + full docs: /Web/theme-code/ in the Snobum repo.
 * ---------------------------------------------------------------------------
 */
(function () {
  try {
    var MAX_PINS = 16;
    var PIN_CLASS = 'pinned-first-product';

    var param = new URLSearchParams(window.location.search).get('first');
    if (!param) return;

    // Comma-separated, in order. Blanks and repeats dropped, capped at MAX_PINS.
    // A single handle (the original form) parses to a one-item list, so every
    // existing ad URL keeps working unchanged.
    var handles = [];
    var seen = {};
    var raw = param.split(',');
    for (var r = 0; r < raw.length && handles.length < MAX_PINS; r++) {
      var h = raw[r].trim();
      // '#' prefix so a handle like "constructor" can't collide with Object.prototype
      if (!h || seen['#' + h]) continue;
      seen['#' + h] = true;
      handles.push(h);
    }
    if (!handles.length) return;

    var run = function () {
      var grid = document.getElementById('product-grid');
      if (!grid) return;
      if (grid.querySelector('.' + PIN_CLASS)) return; // already ran

      // Grab the clone template BEFORE anything is moved or inserted. Reading it
      // later would mean cloning an already-pinned card, whose per-product extras
      // have been stripped.
      var template = grid.querySelector('.grid__item');
      if (!template) return; // empty collection, nothing to clone

      // Resolve every handle to a card, preserving the order given in the URL.
      // All on-page lookups happen now, before any DOM changes.
      var jobs = handles.map(function (handle) {
        var existing = findCard(grid, handle);
        if (existing) {
          existing.classList.add(PIN_CLASS);
          return Promise.resolve({ node: existing, injected: false });
        }
        return fetch('/products/' + encodeURIComponent(handle) + '.js', { headers: { Accept: 'application/json' } })
          .then(function (res) { return res.ok ? res.json() : null; })
          .then(function (p) {
            var card = p ? cloneCard(template, p) : null;
            return card ? { node: card, injected: true } : null;
          })
          .catch(function () { return null; }); // one bad pin never blocks the rest
      });

      // Wait for all of them so the pins land in URL order rather than whichever
      // fetch resolves first.
      Promise.all(jobs).then(function (results) {
        var injected = 0;
        var prev = null;
        for (var i = 0; i < results.length; i++) {
          if (!results[i] || !results[i].node) continue;
          var node = results[i].node;
          // First pin goes to the front, each later one right after the previous
          // pin. A card already sitting in the right place resolves to a no-op.
          grid.insertBefore(node, prev ? prev.nextSibling : grid.firstElementChild);
          prev = node;
          if (results[i].injected) injected++;
        }
        trimTail(grid, injected);
      });
    };

    // Find the card for `handle` among the cards currently on the page.
    function findCard(grid, handle) {
      var cards = grid.children; // Dawn: <li class="grid__item">
      for (var i = 0; i < cards.length; i++) {
        var link = cards[i].querySelector('a[href*="/products/"]');
        if (!link) continue;
        // Exact handle match (avoids "kahlo-meise" matching "kahlo-meise-2")
        var m = link.getAttribute('href').match(/\/products\/([^/?#]+)/);
        if (m && m[1] === handle) return cards[i];
      }
      return null;
    }

    // Every injected card pushes the page past its 16-product count, leaving
    // orphans alone on a new row at the bottom. Drop one real card per injected
    // one so the grid stays full and rectangular. Moved cards don't count (they
    // were already on the page). Never eats a pinned card.
    function trimTail(grid, count) {
      for (var n = 0; n < count; n++) {
        var items = grid.querySelectorAll('.grid__item');
        var last = items.length ? items[items.length - 1] : null;
        if (!last || last.classList.contains(PIN_CLASS) || !last.parentNode) return;
        last.parentNode.removeChild(last);
      }
    }

    function removeAll(root, selector) {
      var els = root.querySelectorAll(selector);
      for (var i = 0; i < els.length; i++) {
        if (els[i].parentNode) els[i].parentNode.removeChild(els[i]);
      }
    }

    // Clone a real card from the page, then repoint it at product `p`.
    function cloneCard(template, p) {
      if (!template) return null; // nothing to clone (empty collection)

      var li = template.cloneNode(true);

      // Strip scroll-reveal animation state so the clone shows immediately
      // (it won't be picked up by the theme's intersection observer).
      li.removeAttribute('data-cascade');
      li.style.removeProperty('--animation-order');
      li.className = li.className.replace(/\banimate--\S+/g, '').replace(/\bscroll-trigger\S*/g, '').trim();
      li.classList.add(PIN_CLASS);

      // Drop cloned ids so several pinned cards on one page can't duplicate them.
      li.removeAttribute('id');
      var withIds = li.querySelectorAll('[id]');
      for (var d = 0; d < withIds.length; d++) withIds[d].removeAttribute('id');

      var url = p.url || ('/products/' + p.handle);

      // Point every product link at our product.
      var links = li.querySelectorAll('a[href*="/products/"]');
      for (var a = 0; a < links.length; a++) links[a].setAttribute('href', url);

      // Title. Dawn renders TWO .card__heading per card (an overlay/no-media
      // heading and the visible one in .card__information), so rewrite them all —
      // querySelector alone would leave the visible title as the cloned card's.
      var headings = li.querySelectorAll('.card__heading');
      for (var h = 0; h < headings.length; h++) {
        (headings[h].querySelector('a') || headings[h]).textContent = p.title;
      }

      // Image: set ours on the first <img>; drop srcset/sizes and any hover image.
      var imgs = li.querySelectorAll('.card__media img');
      if (!imgs.length) imgs = li.querySelectorAll('img');
      var src = p.featured_image || (p.images && p.images[0]) || '';
      if (src) src += (src.indexOf('?') === -1 ? '?' : '&') + 'width=533';
      for (var k = 0; k < imgs.length; k++) {
        if (k === 0 && src) {
          imgs[k].setAttribute('src', src);
          imgs[k].setAttribute('alt', p.title || '');
          imgs[k].removeAttribute('srcset');
          imgs[k].removeAttribute('sizes');
        } else if (k > 0 && imgs[k].parentNode) {
          imgs[k].parentNode.removeChild(imgs[k]); // secondary/hover image
        }
      }

      // Price: rebuild with our product's numbers, matching Dawn's structure.
      var price = li.querySelector('.price');
      if (price) price.outerHTML = priceHtml(p);

      // Remove per-product extras that belong to the cloned product, not ours:
      // review stars (incl. Loox/Judge.me), sale/sold-out badges, quick-add
      // buttons (would add the wrong product to cart), and colour swatches.
      removeAll(li,
        '.rating, .rating-count, .card__reviews, [class*="loox"], [class*="jdgm"], ' +
        '.card__badge, quick-add, .quick-add, [class*="quick-add"], ' +
        '.card__add-to-cart, variant-selects, .swatch, [class*="swatch"]');

      return li;
    }

    // Dawn price markup (component-price.css), built from the PINNED product's own
    // data. `price_min` is the lowest actual variant price (what Dawn shows), and
    // `compare_at_price` is the struck original. "From" only ever prefixes the
    // regular price, never the sale price, to match the other cards.
    function priceHtml(p) {
      var money = function (c) { return '$' + (c / 100).toFixed(2); };
      var price = typeof p.price_min === 'number' ? p.price_min : p.price;
      var compare = p.compare_at_price || p.compare_at_price_max || 0;
      var pref = p.price_varies ? 'From ' : '';
      if (compare && compare > price) {
        return '<div class="price price--on-sale"><div class="price__container">' +
          '<div class="price__regular">' +
            '<span class="price-item price-item--regular">' + money(price) + '</span>' +
          '</div>' +
          '<div class="price__sale">' +
            '<span><s class="price-item price-item--regular">' + money(compare) + '</s></span> ' +
            '<span class="price-item price-item--sale price-item--last">' + money(price) + '</span>' +
          '</div>' +
        '</div></div>';
      }
      return '<div class="price"><div class="price__container"><div class="price__regular">' +
        '<span class="price-item price-item--regular">' + pref + money(price) + '</span>' +
        '</div></div></div>';
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', run);
    } else {
      run();
    }
  } catch (e) {
    /* fail silent — collection stays in its normal order */
  }
})();
